/**
 * Générer un entraînement : les matières, puis les compétences, puis l'aperçu.
 *
 * Hedi, le 27 septembre 2026 : *« Je veux pouvoir ajouter n'importe quel
 * exercice à un entrainement du moment que la compétence a été vue. Je veux que
 * tu me rajoute un tab dans entrainement permettant de générer un entrainement
 * en donnant le nombre d'exerices par compétence voulus dans cet entrainement.
 * Premiere étape : matiere couverte, 2eme étapes : proposition de compétences a
 * couvrir. »*
 *
 * Il vit dans `ui/` depuis le 5 octobre 2026, **déplacé** depuis
 * `v2/GenererScreen` : l'élève de lycée compose désormais ses propres
 * entraînements (« Entraînement », `/eleve/entrainement/generer`), par le même
 * chemin, et la coquille enfant n'importe ni `parent/` ni `v2/`. Recopié, les
 * deux écrans auraient fini par ne plus effacer l'aperçu au même moment — et un
 * aperçu resté sous un choix qu'il ne reflète plus est ajouté tel quel. Ce qui
 * diffère entre le parent et l'élève tient en deux choses passées par
 * l'appelant : **les appels** (`/enfants/{id}/…` contre `/me/…`) et **les
 * phrases** (`TEXTES`), le parent parlant de « l'enfant », l'élève étant
 * tutoyé.
 *
 * **Trois étapes sur un seul écran**, et non trois pages : revenir d'une étape
 * à l'autre ne doit rien perdre de ce qui est coché plus bas. Changer une étape
 * **efface ce qui en dépend** — décocher une matière relit les compétences,
 * changer une compétence ou la difficulté efface l'aperçu —, parce qu'un aperçu
 * resté affiché sous un choix qu'il ne reflète plus serait ajouté tel quel.
 *
 * **Rien n'est généré ni payé.** Les exercices viennent des cahiers achetés,
 * déjà découpés et vérifiés ; le serveur ne propose que des compétences **vues**
 * (une preuve depuis la rentrée, moins ce que l'enfant a déclaré ne pas avoir
 * vu), et il revérifie tout à l'ajout.
 *
 * **« Un autre » ne remplace qu'un exercice.** Le tirage est déterministe : le
 * relancer entier avec une exclusion pourrait déplacer les autres lignes du
 * groupe, déjà regardées. On redemande donc un seul exercice de la compétence,
 * en excluant tous ceux déjà montrés pour elle.
 *
 * **Le corrigé ne traverse pas** (règle 2) : ni la proposition ni le tirage ne
 * le portent, ce que le serveur affirme sur le texte HTTP brut.
 */

import { useEffect, useMemo, useState } from "react";

import { ApiError } from "@/api/errors";
import type {
  CompetenceProposeeOut,
  ExerciceTireOut,
  GroupeTireOut,
  PropositionIn,
  PropositionOut,
  TirageIn,
  TirageOut,
} from "@/api/schema";
import { intituleDExercice } from "@/ui/cahiers";
import { Decoupe, pagesDe } from "@/ui/Decoupe";
import { enClair } from "@/ui/dates";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

export type Mode = NonNullable<TirageIn["mode"]>;

/** Pour qui l'écran est dessiné : le parent qui prépare, l'élève qui se
 *  prépare. */
export type Destinataire = "parent" | "eleve";

/** Les trois réglages de difficulté, dans l'ordre où l'écran les propose. */
export const MODES: { valeur: Mode; libelle: string }[] = [
  { valeur: "progressif", libelle: "Progressif" },
  { valeur: "consolidation", libelle: "Consolidation" },
  { valeur: "defi", libelle: "Défi" },
];

/** Ce qui change d'un destinataire à l'autre, et rien d'autre. */
export const TEXTES: Record<
  Destinataire,
  {
    titre: string;
    chapeau: string;
    aucuneMatiere: string;
    aides: Record<Mode, string>;
    ajouter: string;
    /** Sous le bouton quand la feuille n'a plus la place. */
    tropPlein: (surLaFeuille: number, maximum: number) => string;
    /** Sous le bouton, toujours : ce que l'ajout fait et ne fait pas. */
    apresAjout: string;
  }
> = {
  parent: {
    titre: "Générer un entraînement",
    chapeau: "À partir des cahiers, sur les compétences déjà vues en classe.",
    aucuneMatiere: "Aucune compétence vue en classe n'a d'exercice dans les cahiers de cet enfant.",
    aides: {
      progressif: "Du plus facile au niveau de l'enfant.",
      consolidation: "En dessous de son niveau : de quoi réussir presque tout.",
      defi: "À son niveau : environ une chance sur deux de réussir.",
    },
    ajouter: "Ajouter à la feuille",
    tropPlein: (surLaFeuille, maximum) =>
      `La feuille en cours a déjà ${surLaFeuille} exercice${surLaFeuille > 1 ? "s" : ""} et en accepte ${maximum} au plus : retirez des compétences ou baissez les nombres.`,
    apresAjout:
      "Ils s'ajoutent à la feuille en cours, que l'enfant ne voit pas encore. C'est la clôture de la feuille qui la lui envoie.",
  },
  // Tutoiement sobre, et aucune réussite annoncée d'avance : « une chance sur
  // deux » est une information de préparation pour un parent, une prédiction
  // d'échec pour celui qui va s'y mettre.
  eleve: {
    titre: "Générer un entraînement",
    chapeau: "Depuis tes cahiers, sur ce que tu as déjà vu en classe.",
    aucuneMatiere: "Aucune compétence vue en classe n'a d'exercice dans tes cahiers.",
    aides: {
      progressif: "Du plus facile jusqu'à ton niveau.",
      consolidation: "Sous ton niveau, pour consolider.",
      defi: "À ton niveau, pour chercher.",
    },
    ajouter: "Ajouter à mon entraînement",
    tropPlein: (surLaFeuille, maximum) =>
      `Ta feuille en cours a déjà ${surLaFeuille} exercice${surLaFeuille > 1 ? "s" : ""} et en accepte ${maximum} au plus : retire des compétences ou baisse les nombres.`,
    apresAjout:
      "Ils s'ajoutent à ta feuille en cours, que tes parents voient aussi. Tu la clos quand tu veux lire la correction.",
  },
};

/** Les quatre appels, vers `/enfants/{id}/…` pour le parent, `/me/…` pour
 *  l'élève. L'objet doit être stable d'un rendu à l'autre (`useMemo`) : la
 *  proposition est relue quand il change. */
export type AppelsDeGeneration = {
  proposer: (corps: PropositionIn) => Promise<PropositionOut>;
  tirer: (corps: TirageIn) => Promise<TirageOut>;
  ajouter: (exerciseIds: string[]) => Promise<void>;
};

/** Le plus grand nombre d'exercices qu'une compétence peut demander (le
 *  serveur refuse au-delà). */
const NOMBRE_MAX = 20;

type Ligne = { cochee: boolean; nombre: number };

function erreurDe(echec: unknown, repli: string): string {
  return echec instanceof ApiError ? echec.message : repli;
}

/** « 2 sur 3 demandés » quand le stock est court, rien sinon. */
export function manque(groupe: Pick<GroupeTireOut, "demandes" | "obtenus">): string | null {
  if (groupe.obtenus >= groupe.demandes) return null;
  return `${groupe.obtenus} sur ${groupe.demandes} demandé${groupe.demandes > 1 ? "s" : ""}`;
}

export function Generer({
  pour,
  appels,
  apresAjout,
}: {
  pour: Destinataire;
  appels: AppelsDeGeneration;
  /** Appelé une fois l'ajout accepté : oublier les copies, puis naviguer. */
  apresAjout: () => void;
}) {
  const textes = TEXTES[pour];

  const [proposition, setProposition] = useState<PropositionOut | null>(null);
  // Les matières demandées ; `null` tant que rien n'a été touché, le serveur
  // retenant alors celles qui ont de quoi proposer.
  const [demande, setDemande] = useState<string[] | null>(null);
  const [lignes, setLignes] = useState<Record<string, Ligne>>({});
  const [mode, setMode] = useState<Mode>("progressif");
  const [groupes, setGroupes] = useState<GroupeTireOut[] | null>(null);
  // Les exercices écartés par « un autre », par compétence : ils ne doivent
  // pas revenir au remplacement suivant.
  const [ecartes, setEcartes] = useState<Record<string, string[]>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<null | "proposition" | "tirage" | "ajout">(null);
  const [epuisee, setEpuisee] = useState<string | null>(null);

  // Étape 1 → 2 : relire la proposition à chaque changement de matières.
  useEffect(() => {
    let annule = false;
    setEnCours("proposition");
    setErreur(null);
    appels
      .proposer({ matieres: demande })
      .then((reponse) => {
        if (annule) return;
        setProposition(reponse);
        setLignes(
          Object.fromEntries(
            (reponse.competences ?? []).map((c) => [
              c.competency_id,
              { cochee: c.cochee, nombre: c.nombre },
            ]),
          ),
        );
        setGroupes(null);
        setEcartes({});
      })
      .catch((echec) => {
        if (!annule) setErreur(erreurDe(echec, "La proposition n'a pas pu être lue."));
      })
      .finally(() => {
        if (!annule) setEnCours(null);
      });
    return () => {
      annule = true;
    };
  }, [appels, demande]);

  const matieres =
    demande ?? (proposition?.matieres ?? []).filter((m) => m.cochee).map((m) => m.matiere);

  const competences = proposition?.competences ?? [];
  const demandees = useMemo(
    () =>
      competences
        .filter((c) => lignes[c.competency_id]?.cochee)
        .map((c) => ({
          competency_id: c.competency_id,
          nombre: lignes[c.competency_id]?.nombre ?? 1,
        })),
    [competences, lignes],
  );
  const totalDemande = demandees.reduce((somme, c) => somme + c.nombre, 0);
  const tires = (groupes ?? []).flatMap((g) => g.exercices ?? []);
  const place = proposition ? proposition.lignes_max - proposition.sur_la_feuille : 0;

  const basculerMatiere = (matiere: string) =>
    setDemande(
      matieres.includes(matiere) ? matieres.filter((m) => m !== matiere) : [...matieres, matiere],
    );

  const changerLigne = (competencyId: string, changement: Partial<Ligne>) => {
    setLignes((avant) => ({
      ...avant,
      [competencyId]: { cochee: false, nombre: 1, ...avant[competencyId], ...changement },
    }));
    setGroupes(null);
    setEcartes({});
  };

  const choisirMode = (valeur: Mode) => {
    setMode(valeur);
    setGroupes(null);
    setEcartes({});
  };

  const tirer = async () => {
    setEnCours("tirage");
    setErreur(null);
    setEpuisee(null);
    try {
      const reponse = await appels.tirer({ mode, competences: demandees, exclure: [] });
      setGroupes(reponse.groupes ?? []);
      setEcartes({});
    } catch (echec) {
      setErreur(erreurDe(echec, "Le tirage a échoué."));
    } finally {
      setEnCours(null);
    }
  };

  /** Remplace un seul exercice, à sa place, par un autre de la même compétence. */
  const unAutre = async (groupe: GroupeTireOut, exercice: ExerciceTireOut) => {
    const dejaEcartes = [...(ecartes[groupe.competency_id] ?? []), exercice.exercise_id];
    const montres = (groupe.exercices ?? []).map((e) => e.exercise_id);
    setErreur(null);
    setEpuisee(null);
    try {
      const reponse = await appels.tirer({
        mode,
        competences: [{ competency_id: groupe.competency_id, nombre: 1 }],
        exclure: [...new Set([...montres, ...dejaEcartes])],
      });
      const nouveau = reponse.groupes?.[0]?.exercices?.[0];
      if (!nouveau) {
        setEpuisee(groupe.competency_id);
        return;
      }
      setEcartes((avant) => ({ ...avant, [groupe.competency_id]: dejaEcartes }));
      setGroupes((avant) =>
        (avant ?? []).map((g) =>
          g.competency_id !== groupe.competency_id
            ? g
            : {
                ...g,
                // Remis dans l'ordre du plus facile au plus difficile : le
                // remplaçant n'a pas forcément la difficulté du remplacé.
                exercices: (g.exercices ?? [])
                  .map((e) => (e.exercise_id === exercice.exercise_id ? nouveau : e))
                  .sort((a, b) => a.rang - b.rang),
              },
        ),
      );
    } catch (echec) {
      setErreur(erreurDe(echec, "Le remplacement a échoué."));
    }
  };

  const ajouter = async () => {
    setEnCours("ajout");
    setErreur(null);
    try {
      await appels.ajouter(tires.map((e) => e.exercise_id));
      apresAjout();
    } catch (echec) {
      setErreur(erreurDe(echec, "L'ajout a échoué."));
      setEnCours(null);
    }
  };

  return (
    <Screen title={textes.titre} lead={textes.chapeau}>
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!proposition && !erreur ? <Loading /> : null}

      {proposition ? (
        <Card title="1. Les matières">
          {(proposition.matieres ?? []).length === 0 ? (
            <Empty>{textes.aucuneMatiere}</Empty>
          ) : (
            <ul className="list" data-etape="matieres">
              {(proposition.matieres ?? []).map((m) => (
                <li key={m.matiere} className="list__item">
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={matieres.includes(m.matiere)}
                      onChange={() => basculerMatiere(m.matiere)}
                    />
                    <span>
                      {m.libelle}{" "}
                      <span className="muted small">
                        — {m.competences} compétence{m.competences > 1 ? "s" : ""} vue
                        {m.competences > 1 ? "s" : ""}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      {proposition && matieres.length > 0 ? (
        <Card title="2. Les compétences">
          {enCours === "proposition" ? <Loading /> : null}
          {competences.length === 0 && enCours !== "proposition" ? (
            <Empty>Aucune compétence vue dans ces matières.</Empty>
          ) : null}
          {competences.length > 0 ? (
            <>
              <p className="muted small">
                Classées de la plus urgente à la moins urgente : contrôle annoncé, devoir, vue
                récemment sans avoir été travaillée, fragile, à réviser, maîtrisée.
              </p>
              <ul className="list" data-etape="competences">
                {competences.map((c) => (
                  <LigneDeCompetence
                    key={c.competency_id}
                    competence={c}
                    ligne={lignes[c.competency_id] ?? { cochee: false, nombre: c.nombre }}
                    changer={(changement) => changerLigne(c.competency_id, changement)}
                  />
                ))}
              </ul>
              <fieldset className="check-group">
                <legend>Difficulté</legend>
                {MODES.map((m) => (
                  <label key={m.valeur} className="check">
                    <input
                      type="radio"
                      name="mode"
                      value={m.valeur}
                      checked={mode === m.valeur}
                      onChange={() => choisirMode(m.valeur)}
                    />
                    <span>
                      {m.libelle} <span className="muted small">— {textes.aides[m.valeur]}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
              <div className="screen__actions">
                <Button
                  type="button"
                  disabled={demandees.length === 0 || enCours !== null}
                  onClick={tirer}
                >
                  {enCours === "tirage"
                    ? "Tirage…"
                    : `Voir les ${totalDemande} exercice${totalDemande > 1 ? "s" : ""}`}
                </Button>
              </div>
            </>
          ) : null}
        </Card>
      ) : null}

      {groupes ? (
        <Card title="3. L'aperçu">
          {tires.length === 0 ? (
            <Empty>Aucun exercice disponible pour ces compétences.</Empty>
          ) : null}
          <div data-etape="apercu">
            {groupes.map((groupe) => (
              <section key={groupe.competency_id}>
                <h3>
                  {groupe.competence}{" "}
                  <span className="muted small">— {groupe.matiere_libelle}</span>
                </h3>
                {manque(groupe) ? (
                  <p className="muted small">
                    {manque(groupe)} : les cahiers n'en ont pas davantage à cette difficulté.
                  </p>
                ) : null}
                {epuisee === groupe.competency_id ? (
                  <p className="muted small">Pas d'autre exercice pour cette compétence.</p>
                ) : null}
                <ul className="list">
                  {(groupe.exercices ?? []).map((exercice) => (
                    <li key={exercice.exercise_id} className="list__item list__item--pile">
                      <div>
                        <strong>{intituleDExercice(exercice)}</strong>
                        <p className="muted small">
                          {exercice.ouvrage_titre} — {exercice.chapitre_titre}
                          {exercice.fait_le ? ` · déjà fait le ${enClair(exercice.fait_le)}` : ""}
                        </p>
                      </div>
                      <Decoupe
                        urls={pagesDe(exercice.enonce_urls, exercice.enonce_url)}
                        intitule={intituleDExercice(exercice)}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => unAutre(groupe, exercice)}
                      >
                        Un autre
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
          {tires.length > 0 ? (
            <div className="screen__actions">
              <Button
                type="button"
                disabled={enCours !== null || tires.length > place}
                onClick={ajouter}
              >
                {enCours === "ajout"
                  ? "Ajout…"
                  : `${textes.ajouter} (${tires.length} exercice${tires.length > 1 ? "s" : ""})`}
              </Button>
              {tires.length > place && proposition ? (
                <p className="muted small">
                  {textes.tropPlein(proposition.sur_la_feuille, proposition.lignes_max)}
                </p>
              ) : null}
              <p className="muted small">{textes.apresAjout}</p>
            </div>
          ) : null}
        </Card>
      ) : null}
    </Screen>
  );
}

function LigneDeCompetence({
  competence,
  ligne,
  changer,
}: {
  competence: CompetenceProposeeOut;
  ligne: Ligne;
  changer: (changement: Partial<Ligne>) => void;
}) {
  const maximum = Math.max(1, Math.min(NOMBRE_MAX, competence.disponibles));
  return (
    <li className="list__item list__item--pile">
      <label className="check">
        <input
          type="checkbox"
          checked={ligne.cochee}
          disabled={competence.disponibles === 0}
          onChange={() => changer({ cochee: !ligne.cochee })}
        />
        <div>
          <strong>{competence.competence}</strong>
          <p className="muted small">
            {competence.matiere_libelle} · {competence.raison_texte}
            {competence.vue_le ? ` (vue le ${enClair(competence.vue_le)})` : ""}
          </p>
          <p className="muted small">
            {competence.disponibles} exercice{competence.disponibles > 1 ? "s" : ""} disponible
            {competence.disponibles > 1 ? "s" : ""} sur {competence.stock} dans les cahiers
          </p>
        </div>
      </label>
      {/* Hors du `<label>` : cliquer dans le champ ne doit pas cocher la case. */}
      <label className="small">
        Nombre{" "}
        <input
          type="number"
          min={1}
          max={maximum}
          value={ligne.nombre}
          aria-label={`Nombre d'exercices pour ${competence.competence}`}
          onChange={(evenement) => {
            const valeur = Number.parseInt(evenement.target.value, 10);
            if (Number.isNaN(valeur)) return;
            changer({ nombre: Math.max(1, Math.min(maximum, valeur)), cochee: true });
          }}
        />
      </label>
    </li>
  );
}
